import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { creditsService } from '../services/api';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';
import {
    LayoutDashboard, FileText, Briefcase, Brain,
    BarChart2, Coins, LogOut, Menu, X, Settings, Search, BookOpen,
} from 'lucide-react';

const NAV_ITEMS = [
    { path: '/dashboard', label: 'nav.dashboard', icon: LayoutDashboard },
    { path: '/resumes', label: 'nav.resumes', icon: FileText },
    { path: '/internships', label: 'nav.applications', icon: Briefcase },
    { path: '/career', label: 'nav.career', icon: Brain },
    { path: '/jobs', label: 'nav.jobs', icon: Search },
    { path: '/study', label: 'nav.study', icon: BookOpen },
    { path: '/analytics', label: 'nav.analytics', icon: BarChart2 },
    { path: '/credits', label: 'nav.credits', icon: Coins },
    { path: '/settings', label: 'nav.settings', icon: Settings },
];

function Sidebar() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [credits, setCredits] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    const displayName = user?.profile?.full_name || user?.email?.split('@')[0];
    const initials = displayName?.charAt(0).toUpperCase();

    useEffect(() => {
        creditsService.getPackages()
            .then(res => setCredits(res.data.credits))
            .catch(() => {});
    }, [location.pathname]);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const isActive = (path) => {
        if (path === '/dashboard') return location.pathname === '/dashboard';
        return location.pathname.startsWith(path);
    };

    return (
        <>
            <div className="mobile-topbar">
                <button
                    className="sidebar-hamburger"
                    onClick={() => setMobileOpen(prev => !prev)}
                    aria-label={mobileOpen ? t('sidebar.close_menu') : t('sidebar.open_menu')}
                >
                    {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
                <div className="mobile-topbar-brand">
                    <img
                        src={logo}
                        alt="Career Platform"
                        className="mobile-topbar-logo"
                    />
                    <div className="mobile-topbar-text">
                        <strong>{t('app.name')}</strong>
                        <small>Career AI</small>
                    </div>
                </div>
                <span className="mobile-topbar-accent" />
            </div>

            {/* Overlay — mobile only */}
            {mobileOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`sidebar ${mobileOpen ? 'sidebar-mobile-open' : ''}`}>
                <div className="sidebar-logo">
                    <img
                        src={logo}
                        alt="Career Platform"
                        className="sidebar-logo-img"
                    />
                    <span>Career Platform</span>
                    <button
                        className="sidebar-close-btn"
                        onClick={() => setMobileOpen(false)}
                        aria-label={t('sidebar.close_menu')}
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="sidebar-nav">
                    <p className="sidebar-section-label">{t('sidebar.main_menu')}</p>
                    {NAV_ITEMS.map(item => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                            >
                                <Icon size={16} />
                                <span>{t(item.label)}</span>
                                {item.path === '/credits' && credits !== null && (
                                    <span className="sidebar-credits-badge">{credits}</span>
                                )}
                                {isActive(item.path) && item.path !== '/credits' && (
                                    <span className="sidebar-active-dot" />
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="sidebar-bottom">
                    <div
                        className="sidebar-profile"
                        onClick={() => navigate('/profile')}
                    >
                        <div className="sidebar-avatar">{initials}</div>
                        <div className="sidebar-profile-info">
                            <strong>{displayName}</strong>
                            <small>{user?.profile?.university || t('profile.subtitle')}</small>
                        </div>
                    </div>
                    <button onClick={logout} className="sidebar-logout" title={t('sidebar.sign_out')}>
                        <LogOut size={16} />
                    </button>
                </div>
            </aside>
        </>
    );
}

export default Sidebar;